import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, CheckCircle2, Mail, Euro, Trash2, Calculator } from "lucide-react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import PricingSimulatorDrawer from "@/components/PricingSimulatorDrawer";



// ---------------------------------------------------------------------------
// PontualPricingBlock — gate for "Serviço Pontual" leads (20/02/2026).
// The Atendimento cannot generate estimates on their own — they trigger
// a request via "Pedir preço", which emails admins + consultores. One
// of them then fills in the sessions (datas, horas, valor) and unlocks
// the estimate flow for everyone.
// ---------------------------------------------------------------------------
function _isPontualLead(lead) {
  const q = (lead?.qualification?.service) || {};
  const cont = (q.continuity || "").toLowerCase();
  if (cont.includes("pontual")) return true;
  return (q.service_types || []).some(t => (t || "").toLowerCase().includes("pontual"));
}

export default function PontualPricingBlock({ lead, currentUser, onChanged, autoFocus }) {
  const [busy, setBusy] = useState(false);
  const [reqOpen, setReqOpen] = useState(false);
  const [reqNotes, setReqNotes] = useState("");
  const [priceOpen, setPriceOpen] = useState(false);
  const [sessions, setSessions] = useState([{ date: "", start_time: "09:00", end_time: "11:00", value: "" }]);
  const [pricedNotes, setPricedNotes] = useState("");
  // 06/08/2026 — Atalho para o Consultor abrir o simulador com o
  // preço pontual como referência, sem sair da página do lead.
  const [simulatorOpen, setSimulatorOpen] = useState(false);

  const pontual = _isPontualLead(lead);
  const pricing = lead?.pontual_pricing || null;
  const isRequested = Boolean(pricing?.requested_at);
  const isPriced = Boolean(pricing?.priced_at);
  const role = (currentUser?.role || "").toLowerCase();
  const canPrice = role === "admin" || role === "consultor";

  // Auto-open the pricing modal when the deep-link asks for it.
  // Only useful for admins/consultores — everyone else just sees the
  // banner-level status.
  useEffect(() => {
    if (autoFocus && canPrice && isRequested && !isPriced) {
      setPriceOpen(true);
    }
  }, [autoFocus, canPrice, isRequested, isPriced]);

  if (!pontual) return null;

  const fmtDate = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleString("pt-PT", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    }); } catch { return iso; }
  };

  const requestPrice = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/leads/${lead.id}/pontual/request-price`, {
        notes: reqNotes.trim() || null,
      });
      toast.success(`Pedido enviado a ${r.data.recipients_count} destinatários`);
      setReqOpen(false);
      setReqNotes("");
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro a pedir preço");
    } finally {
      setBusy(false);
    }
  };

  const addSession = () => setSessions(s => [...s, { date: "", start_time: "09:00", end_time: "11:00", value: "" }]);
  const removeSession = (i) => setSessions(s => s.filter((_, idx) => idx !== i));
  const updateSession = (i, patch) => setSessions(s => s.map((sess, idx) => idx === i ? { ...sess, ...patch } : sess));
  const totalPreview = sessions.reduce((acc, s) => acc + (parseFloat(s.value) || 0), 0);

  const savePrice = async () => {
    if (sessions.length === 0) { toast.error("Adicione pelo menos uma sessão"); return; }
    for (const s of sessions) {
      if (!s.date || !s.start_time || !s.end_time) { toast.error("Cada sessão precisa de data, início e fim"); return; }
      if (!(parseFloat(s.value) > 0)) { toast.error("Valor da sessão inválido"); return; }
    }
    setBusy(true);
    try {
      await api.post(`/leads/${lead.id}/pontual/set-price`, {
        sessions: sessions.map(s => ({
          date: s.date, start_time: s.start_time, end_time: s.end_time,
          value: parseFloat(s.value) || 0,
        })),
        notes: pricedNotes.trim() || null,
      });
      toast.success("Preço definido — estimativa desbloqueada");
      setPriceOpen(false);
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro a definir preço");
    } finally {
      setBusy(false);
    }
  };

  // ---- State C: priced (green summary) --------------------------------
  if (isPriced) {
    // Compute the reference the simulator drawer will pin at the top.
    const _sessions = pricing.sessions || [];
    const _sumHours = _sessions.reduce((acc, s) => {
      if (s.hours != null && !isNaN(Number(s.hours))) return acc + Number(s.hours);
      const [sh, sm] = String(s.start_time || "").split(":");
      const [eh, em] = String(s.end_time || "").split(":");
      const startM = (parseInt(sh, 10) || 0) * 60 + (parseInt(sm, 10) || 0);
      let endM = (parseInt(eh, 10) || 0) * 60 + (parseInt(em, 10) || 0);
      if (endM < startM) endM += 24 * 60;
      return acc + (endM - startM) / 60;
    }, 0);
    const _hourly = _sumHours > 0 ? Number(pricing.total_value) / _sumHours : 0;
    const pontualCtx = {
      total_value: Number(pricing.total_value || 0),
      total_hours: Number(_sumHours.toFixed(2)),
      hourly_price: Number(_hourly.toFixed(2)),
      sessions_count: _sessions.length,
    };
    return (
      <>
      <div
        data-testid="pontual-priced-block"
        className="rounded-2xl border border-[hsl(var(--success))]/40 bg-[hsl(var(--success-bg))] p-5"
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-[hsl(var(--success-bg))] border border-[hsl(var(--success))]/40 flex items-center justify-center flex-shrink-0">
            <CheckCircle2 className="w-5 h-5 text-[hsl(var(--success))]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[hsl(var(--success))]">
              Serviço Pontual · Valorizado
            </div>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <h3 className="font-ui text-lg text-[hsl(var(--success))] mt-0.5">
                  Total: <b>{Number(pricing.total_value).toLocaleString("pt-PT", { minimumFractionDigits: 2 })} €</b>
                </h3>
                <div className="text-xs text-[hsl(var(--success))] mt-1">
                  {pricing.sessions?.length || 0} sessões · Valorizado por <b>{pricing.priced_by_name || pricing.priced_by_role}</b> em {fmtDate(pricing.priced_at)}
                </div>
              </div>
              {canPrice && (
                <button
                  type="button"
                  onClick={() => setSimulatorOpen(true)}
                  data-testid="btn-open-simulator-from-pontual"
                  className="inline-flex items-center gap-1.5 text-xs bg-card hover:bg-[hsl(var(--success-bg))] border border-[hsl(var(--success))]/40 text-[hsl(var(--success))] rounded-full px-3 py-1.5 font-semibold shrink-0"
                  title="Abrir o simulador de preços com este pontual como referência"
                >
                  <Calculator className="w-3.5 h-3.5" /> Testar cenários
                </button>
              )}
            </div>
            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-xs" data-testid="pontual-priced-sessions">
                <thead>
                  <tr className="text-left text-[hsl(var(--success))] border-b border-[hsl(var(--success))]/25">
                    <th className="pb-1 pr-3 font-semibold">Data</th>
                    <th className="pb-1 pr-3 font-semibold">Início</th>
                    <th className="pb-1 pr-3 font-semibold">Fim</th>
                    <th className="pb-1 font-semibold text-right">Valor</th>
                  </tr>
                </thead>
                <tbody className="text-[hsl(var(--success))]">
                  {(pricing.sessions || []).map((s, i) => (
                    <tr key={i} className="border-b border-emerald-100 last:border-0">
                      <td className="py-1 pr-3">{s.date}</td>
                      <td className="py-1 pr-3">{s.start_time}</td>
                      <td className="py-1 pr-3">{s.end_time}</td>
                      <td className="py-1 text-right">{Number(s.value || 0).toLocaleString("pt-PT", { minimumFractionDigits: 2 })} €</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {pricing.priced_notes && (
              <div className="text-xs text-[hsl(var(--success))] mt-2 pt-2 border-t border-[hsl(var(--success))]/25">
                <b>Notas:</b> {pricing.priced_notes}
              </div>
            )}
          </div>
        </div>
      </div>
      <PricingSimulatorDrawer
        open={simulatorOpen}
        onClose={() => setSimulatorOpen(false)}
        pontualContext={pontualCtx}
      />
      </>
    );
  }

  // ---- State B: requested but not priced --------------------------------
  if (isRequested) {
    return (
      <>
        <div
          data-testid="pontual-waiting-block"
          className="rounded-2xl border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning-bg))] p-5"
        >
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-[hsl(var(--warning-bg))] border border-[hsl(var(--warning))]/40 flex items-center justify-center flex-shrink-0">
              <Loader2 className="w-5 h-5 text-[hsl(var(--warning))] animate-spin-slow" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[hsl(var(--warning))]">
                Serviço Pontual · À espera de valorização
              </div>
              <h3 className="font-ui text-lg text-[hsl(var(--warning))] mt-0.5">
                Aguardar preço do admin ou Consultor de Cuidados
              </h3>
              <div className="text-xs text-[hsl(var(--warning))] mt-1">
                Pedido enviado por <b>{pricing.requested_by_name || "Atendimento"}</b> em {fmtDate(pricing.requested_at)}
              </div>
              {pricing.request_notes && (
                <div className="text-xs text-[hsl(var(--warning))] mt-2 p-2 bg-card/60 rounded border border-[hsl(var(--warning))]/25">
                  <b>Notas do Atendimento:</b> {pricing.request_notes}
                </div>
              )}
              {Array.isArray(pricing.request_recipients) && pricing.request_recipients.length > 0 && (
                <details className="mt-2" data-testid="pontual-request-recipients">
                  <summary className="text-[11px] cursor-pointer text-[hsl(var(--warning))] font-semibold uppercase tracking-widest">
                    Notificados por email ({pricing.request_recipients.length})
                  </summary>
                  <div className="mt-2 p-2 bg-card/60 rounded border border-[hsl(var(--warning))]/25 space-y-1">
                    {pricing.request_recipients.map((r, i) => (
                      <div key={i} className="text-[11px] text-[hsl(var(--warning))] flex items-center gap-2">
                        <span className="inline-block text-[9px] uppercase tracking-widest text-[hsl(var(--warning))] font-semibold min-w-[62px]">
                          {r.role === "admin" ? "Admin" : "Consultor(a)"}
                        </span>
                        <span className="font-medium">{r.name || "—"}</span>
                        <span className="text-[hsl(var(--warning))]/70">&lt;{r.email}&gt;</span>
                      </div>
                    ))}
                  </div>
                </details>
              )}
              <div className="mt-3 text-[11px] text-[hsl(var(--warning))] leading-relaxed">
                O Atendimento não pode gerar a estimativa até o valor ser definido.
                A estimativa fica automaticamente desbloqueada assim que o admin/consultor guardar.
              </div>
              {canPrice && (
                <button
                  data-testid="btn-pontual-set-price"
                  onClick={() => setPriceOpen(true)}
                  disabled={busy}
                  className="mt-3 inline-flex items-center gap-2 bg-foreground hover:bg-foreground/90 text-background rounded-full px-4 py-2 text-xs font-semibold"
                >
                  <Euro className="w-3.5 h-3.5" /> Definir valor
                </button>
              )}
            </div>
          </div>
        </div>
        {canPrice && <PontualPriceModal
          open={priceOpen}
          onClose={() => setPriceOpen(false)}
          sessions={sessions}
          setSessions={setSessions}
          notes={pricedNotes}
          setNotes={setPricedNotes}
          addSession={addSession}
          removeSession={removeSession}
          updateSession={updateSession}
          totalPreview={totalPreview}
          busy={busy}
          onSave={savePrice}
        />}
      </>
    );
  }

  // ---- State A: not requested yet ---------------------------------------
  return (
    <>
      <div
        data-testid="pontual-request-block"
        className="rounded-2xl border border-[#B5895A] bg-muted/40 p-5"
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-[#B5895A]/15 border border-[#B5895A]/30 flex items-center justify-center flex-shrink-0">
            <Euro className="w-5 h-5 text-[#B5895A]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#B5895A]">
              Serviço Pontual · Sem preço definido
            </div>
            <h3 className="font-ui text-lg text-foreground mt-0.5">
              A estimativa está bloqueada até o admin/consultor definir o valor
            </h3>
            <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">
              Serviços pontuais precisam de valorização caso-a-caso. Carregue no botão abaixo
              para enviar um resumo do pedido (contacto, datas, horas, necessidades) aos admins e
              Consultores de Cuidados por email. Assim que um deles definir o valor, a estimativa é desbloqueada.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                data-testid="btn-pontual-request-price"
                onClick={() => setReqOpen(true)}
                disabled={busy}
                className="inline-flex items-center gap-2 bg-foreground hover:bg-foreground/90 text-background rounded-full px-4 py-2 text-xs font-semibold"
              >
                <Mail className="w-3.5 h-3.5" /> Pedir preço a admin/consultor
              </button>
              {canPrice && (
                <button
                  data-testid="btn-pontual-set-price-direct"
                  onClick={() => setPriceOpen(true)}
                  className="inline-flex items-center gap-2 bg-card hover:bg-muted/40 text-foreground border border-border rounded-full px-4 py-2 text-xs font-semibold"
                >
                  <Euro className="w-3.5 h-3.5" /> Definir valor directamente
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Request modal — small notes textarea */}
      <Dialog open={reqOpen} onOpenChange={(o) => { if (!busy) setReqOpen(o); }}>
        <DialogContent data-testid="pontual-request-modal" className="max-w-md">
          <DialogHeader>
            <DialogTitle>Pedir preço a admin/consultor</DialogTitle>
            <DialogDescription>
              Vamos enviar um email a todos os admins e Consultores de Cuidados activos com
              o resumo do lead (contactos, datas, horas, tipos de serviço, necessidades).
              O primeiro a valorizar fecha o processo.
            </DialogDescription>
          </DialogHeader>
          <div className="pt-2">
            <label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold">
              Notas para quem for valorizar (opcional)
            </label>
            <textarea
              data-testid="pontual-request-notes"
              className="w-full mt-1 bg-card border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-primary"
              rows={3}
              value={reqNotes}
              onChange={(e) => setReqNotes(e.target.value)}
              placeholder="Ex: Cliente urgente, disponibilidade limitada, etc."
              disabled={busy}
              maxLength={500}
            />
          </div>
          <DialogFooter className="pt-4">
            <button
              data-testid="pontual-request-cancel"
              onClick={() => setReqOpen(false)}
              disabled={busy}
              className="inline-flex items-center gap-1.5 text-xs bg-muted hover:bg-muted border border-border rounded-full px-4 py-2 font-semibold"
            >
              Cancelar
            </button>
            <button
              data-testid="pontual-request-confirm"
              onClick={requestPrice}
              disabled={busy}
              className="inline-flex items-center gap-2 bg-foreground hover:bg-foreground/90 text-background rounded-full px-4 py-2 text-xs font-semibold disabled:opacity-40"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
              Enviar pedido
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {canPrice && <PontualPriceModal
        open={priceOpen}
        onClose={() => setPriceOpen(false)}
        sessions={sessions}
        setSessions={setSessions}
        notes={pricedNotes}
        setNotes={setPricedNotes}
        addSession={addSession}
        removeSession={removeSession}
        updateSession={updateSession}
        totalPreview={totalPreview}
        busy={busy}
        onSave={savePrice}
      />}
    </>
  );
}


function PontualPriceModal({ open, onClose, sessions, addSession, removeSession, updateSession,
                              notes, setNotes, totalPreview, busy, onSave }) {
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!busy) onClose(); }}>
      <DialogContent data-testid="pontual-price-modal" className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Definir valor do serviço pontual</DialogTitle>
          <DialogDescription>
            Adicione uma linha por sessão (data, hora de início e fim, valor). O total é a soma dos valores.
            Assim que gravar, o Atendimento fica com a estimativa desbloqueada.
          </DialogDescription>
        </DialogHeader>
        <div className="pt-2 space-y-3">
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="pontual-price-sessions">
              <thead>
                <tr className="text-left text-muted-foreground border-b border-border">
                  <th className="pb-2 pr-2 font-semibold uppercase tracking-[0.1em] text-[10px]">Data</th>
                  <th className="pb-2 pr-2 font-semibold uppercase tracking-[0.1em] text-[10px]">Início</th>
                  <th className="pb-2 pr-2 font-semibold uppercase tracking-[0.1em] text-[10px]">Fim</th>
                  <th className="pb-2 pr-2 font-semibold uppercase tracking-[0.1em] text-[10px] text-right">Valor (€)</th>
                  <th className="pb-2 w-8"></th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((s, i) => (
                  <tr key={i} data-testid={`pontual-session-row-${i}`} className="border-b border-border/60">
                    <td className="py-1.5 pr-2">
                      <input
                        data-testid={`pontual-session-date-${i}`}
                        type="date"
                        className="w-full bg-card border border-border rounded-md px-2 py-1.5 text-sm"
                        value={s.date}
                        onChange={(e) => updateSession(i, { date: e.target.value })}
                        disabled={busy}
                      />
                    </td>
                    <td className="py-1.5 pr-2">
                      <input
                        data-testid={`pontual-session-start-${i}`}
                        type="time"
                        className="w-full bg-card border border-border rounded-md px-2 py-1.5 text-sm"
                        value={s.start_time}
                        onChange={(e) => updateSession(i, { start_time: e.target.value })}
                        disabled={busy}
                      />
                    </td>
                    <td className="py-1.5 pr-2">
                      <input
                        data-testid={`pontual-session-end-${i}`}
                        type="time"
                        className="w-full bg-card border border-border rounded-md px-2 py-1.5 text-sm"
                        value={s.end_time}
                        onChange={(e) => updateSession(i, { end_time: e.target.value })}
                        disabled={busy}
                      />
                    </td>
                    <td className="py-1.5 pr-2">
                      <input
                        data-testid={`pontual-session-value-${i}`}
                        type="number"
                        step="0.01"
                        min="0"
                        className="w-full bg-card border border-border rounded-md px-2 py-1.5 text-sm text-right"
                        value={s.value}
                        onChange={(e) => updateSession(i, { value: e.target.value })}
                        placeholder="0,00"
                        disabled={busy}
                      />
                    </td>
                    <td className="py-1.5">
                      <button
                        data-testid={`pontual-session-remove-${i}`}
                        onClick={() => removeSession(i)}
                        disabled={busy || sessions.length <= 1}
                        className="text-[hsl(var(--danger))] hover:text-[hsl(var(--danger))] disabled:opacity-30 disabled:cursor-not-allowed"
                        title="Remover sessão"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button
            data-testid="pontual-session-add"
            onClick={addSession}
            disabled={busy}
            className="text-xs inline-flex items-center gap-1 text-muted-foreground hover:text-foreground border border-dashed border-border rounded-md px-3 py-1.5"
          >
            + Adicionar sessão
          </button>

          <div className="flex items-center justify-between bg-muted/40 border border-border rounded-lg px-4 py-3">
            <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold">
              Total previsto
            </div>
            <div
              data-testid="pontual-total-preview"
              className="text-xl font-ui text-foreground"
            >
              {totalPreview.toLocaleString("pt-PT", { minimumFractionDigits: 2 })} €
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold">
              Notas internas (opcional)
            </label>
            <textarea
              data-testid="pontual-price-notes"
              className="w-full mt-1 bg-card border border-border rounded-lg px-3 py-2 text-sm"
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Ex: valor a pronto pagamento, desconto aplicado, etc."
              disabled={busy}
              maxLength={500}
            />
          </div>
        </div>
        <DialogFooter className="pt-2">
          <button
            data-testid="pontual-price-cancel"
            onClick={onClose}
            disabled={busy}
            className="inline-flex items-center gap-1.5 text-xs bg-muted hover:bg-muted border border-border rounded-full px-4 py-2 font-semibold"
          >
            Cancelar
          </button>
          <button
            data-testid="pontual-price-save"
            onClick={onSave}
            disabled={busy || totalPreview <= 0}
            className="inline-flex items-center gap-2 bg-foreground hover:bg-foreground/90 text-background rounded-full px-4 py-2 text-xs font-semibold disabled:opacity-40"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
            Gravar e desbloquear estimativa
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}



